import Head from "next/head";
import { Content, GoogleJobsSchema } from "@/ui";
import { getRoute } from "@/getters/getRoute";
import { getGlobal } from "@/getters/getGlobal";
import { createTitle } from "@/functions/createTitle";
// import { jobSearch } from "@/functions/jobSearch";
import { similarJobs } from "@/functions/similarJobs";
import { jobs_helper } from "@/helpers/jobs_helper";
import * as additionalComponents from "./__components";
import useDynamicPage from "@/hooks/useDynamicPage";
import { getDynamicPageStaticProps } from "@/functions/getDynamicPageStaticProps";
import { DynamicPageSeo } from "@/ui/DynamicPageSeo";
import { ContentBlocks } from "@/ui/ContentBlocks";

export default function JobPage({ data, prefix, title, canonical, seo, isBuilder, job, content }) {
  const global = getGlobal();
  let job_types = null;
  let [employment_type] = [];
  
  if (!isBuilder) {
    job_types = jobs_helper.getCategoryValues("275d8990-bd9e-4f79-a0e2-d81bb734c855", job);
    [employment_type] = job_types.map(
      (i) =>
        ({
          "03ae7fb9-715c-44bd-9c2e-ed484c5a1828": "FULL_TIME", // Permanent
          "de22c745-1acc-4ebb-b979-eff86a3bd7a3": "FULL_TIME",
          "d9105bf6-e762-4748-80aa-072e00e6c8a9": "PART_TIME",
          "a6fe610b-bf48-46a1-aaef-43ad2c88a925": "CONTRACTOR",
          "7d5b3acb-dd46-4d1b-9fd7-d29446eba46a": "TEMPORARY",
        })[i.id],
    );
  }

  return (
    <>
      {isBuilder ? (
        <>
          <DynamicPageSeo
            {...seo}
            title={seo?.title.length > 0 ? seo.title : title}
            canonical={seo?.canonical.length > 0 ? seo.canonical : canonical}
          />
          <ContentBlocks data={data} prefix={prefix} />
        </>
      ) : (
        <>
          <Head>
            <meta property="og:title" content={job.title} />
            <meta property="og:url" content={`/jobs/${job.url_slug}`} />
            <meta name="twitter:title" content={job.title} />
            <meta
              name="image"
              property="og:image"
              content={job.image ?? "https://cdn.sourceflow.co.uk/8zwqr8r5l73cstbidhd4ur2k26cs"}
            />
            <meta name="twitter:image" content={job.image ?? "https://cdn.sourceflow.co.uk/8zwqr8r5l73cstbidhd4ur2k26cs"} />
          </Head>
          <GoogleJobsSchema
            job={job}
            employment_type={employment_type}
            site_name={global["_theme.company.name"]}
            site_domain_link={global["_theme.company.domain"]}
            site_google_logo_link={"https://cdn.sourceflow.co.uk/8zwqr8r5l73cstbidhd4ur2k26cs"}
          />
          <Content items={content} additionalComponents={additionalComponents} />
        </>
      )}
    </>
  );
}

export async function getStaticProps({ params: { url_slug } }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const builderPage = useDynamicPage(`jobs/${url_slug}`);
  if (builderPage) {
    return getDynamicPageStaticProps({ page: builderPage, url_slug });
  }

  const job = jobs_helper.find(url_slug);
  const sectorsIds = jobs_helper.getCategoryValueIds("3186657c-e89c-4a6f-9157-35eb7fe0b379", job);
  const JobTypeIds = jobs_helper.getCategoryValueIds("275d8990-bd9e-4f79-a0e2-d81bb734c855", job);

  // const related = (await jobSearch({
  //   limit: 6,
  //   query: job.title ?? null,
  //   location: job.location ?? null,
  //   filters: {
  //     "3186657c-e89c-4a6f-9157-35eb7fe0b379": sectorsIds,
  //     "275d8990-bd9e-4f79-a0e2-d81bb734c855": JobTypeIds,
  //   }
  // })).filter((i) => i.id != job.id);

  const related = (await similarJobs({
    query: job.title ?? null,
    lat: job?.derived_info.locations[0]?.lat_lng.latitude,
    lng: job?.derived_info.locations[0]?.lat_lng.longitude,
    radius: 10,
    category_filters: JobTypeIds,
  })).filter((i) => i.id != job.id);

  const similar = jobs_helper.fetch({
    limit: 4,
    exclude: [job.id],
    filter: (i) => 
      (sectorsIds.some((s) => JSON.stringify(i.categories).includes(s)) ||
      JobTypeIds.some((s) => JSON.stringify(i.categories).includes(s))) &&
      job.location.split(", ").pop() == i.location.split(", ").pop()
  });

  return {
    props: {
      meta: {
        title: createTitle(job.title),
        canonical: "https://www.bluearrow.co.uk" + getRoute("job", { url_slug: url_slug })
      },
      job,
      content: [
        {
          component: "BreadcrumbNavigation",
          props: {
            items: [
              { label: "Find a Job", href: getRoute("jobs") },
              { label: job.title, href: getRoute("job", { url_slug }) },
            ],
          },
        },
        {
          component: "Header",
          props: {
            className: "text-tertiary",
            title: job.title,
            back: {
              path: "page.job.component.Header.back",
              placeholder: "Back to job search",
              href: getRoute("jobs"),
            },
          },
        },
        {
          component: "SimilarJobs",
          props: {
            items: similar.map((i) => ({
              ...i,
              href: getRoute("job", { url_slug: i.url_slug }),
            })),
          },
        },
        {
          component: "JobPageContent",
          props: {
            ...job,
            related: related.map((i) => ({
              ...i,
              location: i.addresses,
              href: getRoute("job", { url_slug: i.url_slug }),
              published_at: (new Date(i.published_at)).getTime() / 1000,
            })),
          },
        },
      ],
    },
  };
}

export async function getStaticPaths() {
  return { paths: jobs_helper.toPaths(), fallback: false };
}
